import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import CustomConfirmModal from "../components/CustomConfirmModal";
import { doc, getDoc } from "firebase/firestore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

interface LoginPageProps {
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginPage: React.FC<LoginPageProps> = ({ setIsAuthenticated }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [crewsHovered, setCrewsHovered] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        username,
        password,
      );
      const user = userCredential.user;

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error("사용자 정보가 존재하지 않습니다.");
      }

      const userData = userDoc.data();
      const userRole = userData.role;

      setIsAuthenticated(true);

      if (userRole === "admin") {
        navigate("/admin/main");
      } else {
        navigate("/user/main");
      }
    } catch (error: any) {
      setError("아이디 혹은 비밀번호를 다시 확인해주세요.");
      setIsModalVisible(true);
    }
  };

  const isButtonEnabled = username !== "" && password !== "";

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (error) setError("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) setError("");
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setError("");
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === "Escape") {
        closeModal();
      }
    };

    if (isModalVisible) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalVisible]);

  return (
    <div className="min-h-screen overflow-hidden bg-white">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[6fr_4fr] lg:gap-6 xl:gap-8">
        <div className="relative min-h-[360px] overflow-hidden bg-white lg:min-h-screen">
          <div className="absolute inset-0 h-full w-full">
            <video
              className="h-full w-full object-cover"
              style={{ objectPosition: "22% center" }}
              autoPlay
              muted
              playsInline
            >
              <source src="/login_film.mp4" type="video/mp4" />
            </video>
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.55) 100%)",
              }}
            />
            <div className="absolute inset-0 p-6 lg:p-12">
              <div className="login-hero-copy absolute bottom-10 left-6 right-6 lg:bottom-auto lg:left-auto lg:right-12 lg:top-[47%] lg:-translate-y-1/2 xl:right-16">
                <p
                  className="login-hero-tagline mb-2 text-sm font-light tracking-[0.00em] text-white/85 lg:text-right lg:text-2xl"
                  style={{
                    textShadow: "0 4px 20px rgba(0, 0, 0, 1)",
                  }}
                >
                  우리 사무실에 딱 맞는 스낵 복지
                </p>
                <h1
                  className="login-hero-title whitespace-nowrap text-3xl font-extrabold leading-tight text-white lg:text-right lg:text-5xl"
                  style={{
                    textShadow: "0 4px 20px rgba(0, 0, 0, 1)",
                  }}
                >
                  오피스버디 스낵 솔루션
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex items-center justify-center bg-white px-6 py-10 lg:px-0 lg:py-0 ml-20">
          <img
            src="https://buddie.kr/images/logo_black.png"
            alt="Buddie"
            className="absolute right-6 top-6 w-32 object-contain lg:right-10 lg:top-10 lg:w-30"
          />
          <div style={{ maxWidth: 520, width: "100%" }}>
            <h2 className="text-left text-2xl font-extrabold">로그인</h2>
            <div
              className="mt-6 space-y-4"
              style={{ width: "80%", maxWidth: 494 }}
            >
              <div className="space-y-2">
                <label className="text-sm font-semibold">이메일 주소</label>
                <Input
                  placeholder="이메일 주소를 입력해 주세요."
                  value={username}
                  onChange={handleUsernameChange}
                  style={{ borderRadius: "8px", height: "45px" }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">비밀번호</label>
                <Input
                  type="password"
                  placeholder="비밀번호를 입력해 주세요."
                  value={password}
                  onChange={handlePasswordChange}
                  style={{ borderRadius: "8px", height: "45px" }}
                />
              </div>

              <Button
                onClick={handleLogin}
                className="h-[45px] w-full font-bold"
                style={{
                  borderRadius: "8px",
                  backgroundColor: hovered
                    ? "#f3653d"
                    : isButtonEnabled
                      ? "#FB5120"
                      : "#d3d3d3",
                }}
                disabled={!isButtonEnabled}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                로그인
              </Button>

              <Button
                variant="outline"
                className="h-[45px] w-full font-bold"
                style={{
                  borderRadius: "8px",
                  backgroundImage: crewsHovered
                    ? "linear-gradient(270deg, #FB5120 0%, #fd9478 100%)"
                    : "linear-gradient(270deg, #ffffff 0%, #ffffff 100%)",
                  border: crewsHovered ? "0px" : "1px solid #FB5120",
                  color: crewsHovered ? "#ffffff" : "#FB5120",
                }}
                onMouseEnter={() => setCrewsHovered(true)}
                onMouseLeave={() => setCrewsHovered(false)}
                onClick={() => {
                  setError("");
                  navigate("/register");
                }}
              >
                회원가입
              </Button>
            </div>
          </div>
        </div>
      </div>

      <a
        href="https://buddie.kr"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "block",
          textDecoration: "none",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
          padding: 0,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            backgroundColor: "#000000",
            borderRadius: "0px 0px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 60px",
            margin: "0 auto",
            minHeight: "56px",
            width: "100%",
            gap: "16px",
            color: "#ffffff",
          }}
        >
          <div
            style={{
              color: "#999999",
              fontSize: "clamp(10px, 0.72vw, 13px)",
              fontWeight: 800,
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "flex-start",
              flex: "1 1 auto",
              lineHeight: "1.2",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontWeight: 800 }}>(주)이십사쩜칠</span>
            <span style={{ fontWeight: 800 }}>|</span>
            <span style={{ fontWeight: 400 }}>
              대표{" "}
              <span
                style={{
                  color: "#ffffff",
                  fontWeight: 800,
                  marginLeft: "6px",
                }}
              >
                이선욱
              </span>
            </span>
            <span style={{ fontWeight: 800 }}>|</span>
            <span style={{ fontWeight: 400 }}>
              사업자등록번호{" "}
              <span
                style={{
                  color: "#ffffff",
                  fontWeight: 800,
                  marginLeft: "6px",
                }}
              >
                864-86-02783
              </span>
            </span>
            <span style={{ fontWeight: 800 }}>|</span>
            <span style={{ fontWeight: 400 }}>
              상담 및 문의{" "}
              <span
                style={{
                  color: "#ffffff",
                  fontWeight: 800,
                  marginLeft: "6px",
                }}
              >
                070-4102-2407
              </span>
            </span>
          </div>
          <div
            style={{
              color: "#777777",
              fontSize: "clamp(10px, 0.64vw, 11px)",
              fontWeight: 500,
              flexShrink: 0,
              lineHeight: "1.2",
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            © 2026 OfficeBuddie. All rights reserved.
          </div>
        </div>
      </a>

      <CustomConfirmModal
        visible={isModalVisible}
        onConfirm={closeModal}
        onCancel={closeModal}
        title="알림"
        content={error}
        width={320}
      />
    </div>
  );
};

export default LoginPage;
